import React, { useEffect, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { NextPage, NextPageContext } from 'next'
import { NextSeo } from 'next-seo'
import { Store } from 'redux'
import { useSelector } from 'react-redux'
import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { institutionItemSelectors } from '@ucheba/store/institutions/item'
import { divisionsSelector } from '@ucheba/store/institutions/divisions'
import { setParams } from '@ucheba/utils/helpers/analytics/ga'
import { EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { IUzPageProps } from '../../../../../components/LayoutInstitution/types'
import Custom404 from '../../../../404.page'
import { useUserActivity } from '../../../../../../utils/hooks/useUserActivity'
import { getInstitutionUnitsPageProps } from '../../../bll/getInstitutionUnitsPageProps'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('./desktop')),
  touch: dynamic(() => import('./touch')),
}

const InstitutionUnitsPage: NextPage<IUzPageProps> = (props) => {
  const { content, notFound, canonical } = props
  const currentInstitution = useSelector(institutionItemSelectors.currentInstitution)

  const params = useMemo(() => {
    const obj: any = {}

    if (currentInstitution) {
      obj.dimension1 = currentInstitution.parent?.id || currentInstitution.id

      if (currentInstitution.parent?.id) {
        obj.dimension2 = currentInstitution.id
      }
    }

    return obj
  }, [currentInstitution])

  setParams(params)

  const { total } = useSelector(divisionsSelector.divisions)

  const { setUserActivity } = useUserActivity()

  useEffect(() => {
    const { href } = window.location

    if (currentInstitution) {
      setUserActivity({
        activityType: EActivityTypes.viewing_institution_info,
        institutionId: currentInstitution.id,
        data: { url: href },
      })
    }
  }, [])

  if (notFound) {
    return <Custom404 />
  }

  return (
    <>
      <NextSeo
        title={content.seo.meta.title}
        description={content.seo.meta.description}
        noindex={!total}
        nofollow={!total}
        canonical={canonical}
        additionalMetaTags={[
          {
            property: 'keywords',
            content: content.seo.meta.keywords,
          },
        ]}
      />

      {getIDeviceComponent(props, devicesPages)}
    </>
  )
}

InstitutionUnitsPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { store } = ctx

  const { notFound, content } = await getInstitutionUnitsPageProps({ ctx, store })

  if (notFound) {
    if (ctx?.res) {
      ctx.res.statusCode = 404
    }

    return {
      notFound: true,
    }
  }

  return {
    content,
  }
}

export default InstitutionUnitsPage
